import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GoldButton, OutlineButton, Card, EmptyState, ui } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeftRight, ArrowDownLeft, ArrowUpRight, Download, Inbox, Sparkles, Pencil as PencilIcon, FileSearch } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
import ExportDialog from "@/components/ExportDialog";

const EXPENSE_CATS = ["Groceries", "Dining", "Travel", "Utilities", "Rent", "Office", "Supplies", "Healthcare", "Entertainment", "Other"];
const INCOME_CATS = ["Salary", "Business", "Dividends", "Interest", "Rental", "Gift", "Other"];
const today = () => new Date().toISOString().slice(0, 10);
const blank = { type: "expense", amount: "", category: "Groceries", description: "", date: today(), account: "Personal" };

export default function Transactions() {
  const { user, hasModule } = useAuth();
  const cur = user?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [filter, setFilter] = useState("all");
  const [exportOpen, setExportOpen] = useState(false);

  const load = () => api.get("/transactions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = (type = "expense") => {
    setEditing(null);
    setForm({ ...blank, type, category: type === "income" ? "Salary" : "Groceries" });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editing ? "Updated" : "Added to your home");
      setOpen(false); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/transactions/${id}`); toast.success("Removed"); load(); };

  const shown = items.filter((i) => filter === "all" || i.type === filter);
  const cats = form.type === "income" ? INCOME_CATS : EXPENSE_CATS;

  return (
    <div>
      <PageHeader title="Cash Flow" subtitle="Every dollar in and out — calm, clear and tidy."
        action={
          <div className="flex gap-2">
            {hasModule("exports") && <OutlineButton data-testid="export-csv-btn" onClick={() => setExportOpen(true)}><Download size={16} /> Export</OutlineButton>}
            <OutlineButton data-testid="add-income-btn" onClick={() => openNew("income")}><ArrowDownLeft size={16} /> Income</OutlineButton>
            <GoldButton data-testid="add-expense-btn" onClick={() => openNew("expense")}><Plus size={16} /> Expense</GoldButton>
          </div>
        } />

      <div className="flex gap-2 mb-5">
        {["all", "expense", "income"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${
              filter === f ? "bg-[#C6A45C] text-white" : "bg-white/60 text-[#8A7F70] hover:text-[#2C2A28] border border-[#EAE0D2]"}`}>
            {f}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        {shown.length === 0 ? (
          <EmptyState icon={ArrowLeftRight} title="Nothing here yet" hint="Add your first income or expense to begin." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="text-xs uppercase tracking-[0.1em] text-[#A99F90] border-b border-[#EAE0D2]">
                  <th className="text-left py-4 px-6 font-normal">Date</th>
                  <th className="text-left py-4 px-3 font-normal">Description</th>
                  <th className="text-left py-4 px-3 font-normal">Category</th>
                  <th className="text-right py-4 px-3 font-normal">Amount</th>
                  <th className="text-right py-4 px-6 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((it) => (
                  <tr key={it.id} className="border-b border-[#F0E9DD] hover:bg-white/60 transition-colors" data-testid={`txn-row-${it.id}`}>
                    <td className="py-4 px-6 text-[#8A7F70] font-mono-data text-xs">{it.date}</td>
                    <td className="py-4 px-3 text-[#2C2A28]">
                      <div className="flex items-center gap-2">
                        {it.type === "income" ? <ArrowDownLeft size={14} className="text-[#5a9a6a]" /> : <ArrowUpRight size={14} className="text-[#b06b6b]" />}
                        <span>{it.description || it.category}</span>
                        {it.source?.origin === "financial_inbox" && (
                          <>
                            <span data-testid={`txn-source-${it.id}`} title={`From Financial Inbox · ${it.source.extraction === "ai" ? "AI extracted" : "edited by user"}${it.source.confidence ? ` · AI confidence ${Math.round(it.source.confidence * 100)}%` : ""}${it.source.upload_method === "email" ? " · emailed" : ""} · confirmed by ${it.source.confirmed_by || "user"}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#8a6d2f] bg-[#C6A45C]/12 rounded-full px-2 py-0.5">
                              {it.source.extraction === "ai" ? <Sparkles size={10} /> : <PencilIcon size={10} />} Inbox
                            </span>
                            {it.source.document_id && (
                              <a data-testid={`txn-view-source-${it.id}`} href={`${BACKEND}/api/documents/${it.source.document_id}/file`}
                                target="_blank" rel="noreferrer" title="View source document"
                                className="inline-flex items-center gap-1 text-[10px] text-[#8a6d2f] hover:underline">
                                <FileSearch size={11} /> Source
                              </a>
                            )}
                          </>
                        )}
                      </div>
                      <span className="text-xs text-[#B6AD9F]">{it.account}</span>
                    </td>
                    <td className="py-4 px-3 text-[#8A7F70]">{it.category}</td>
                    <td className={`py-4 px-3 text-right font-mono-data ${it.type === "income" ? "text-[#5a9a6a]" : "text-[#2C2A28]"}`}>
                      {it.type === "income" ? "+" : "−"}{fmt(it.amount, cur).replace("-", "")}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button data-testid={`edit-txn-${it.id}`} onClick={() => openEdit(it)} className="text-[#8A7F70] hover:text-[#C6A45C] p-1.5"><Pencil size={15} /></button>
                      <button data-testid={`delete-txn-${it.id}`} onClick={() => remove(it.id)} className="text-[#8A7F70] hover:text-[#C99FA0] p-1.5"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={ui.dialog}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">{editing ? "Edit Transaction" : `New ${form.type === "income" ? "Income" : "Expense"}`}</DialogTitle>
            <DialogDescription className="text-[#8A7F70]">Record money moving in or out of your home.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={ui.label}>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: v === "income" ? "Salary" : "Groceries" })}>
                  <SelectTrigger data-testid="txn-type" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                  <SelectContent className={ui.selectContent}>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={ui.label}>Amount ({cur})</Label>
                <Input data-testid="txn-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={ui.input} placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={ui.label}>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="txn-category" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                  <SelectContent className={ui.selectContent}>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className={ui.label}>Date</Label>
                <Input data-testid="txn-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={ui.input} />
              </div>
            </div>
            <div>
              <Label className={ui.label}>Description</Label>
              <Input data-testid="txn-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={ui.input} placeholder="e.g. Woolworths weekly shop" />
            </div>
          </div>
          <DialogFooter>
            <GoldButton data-testid="save-txn-btn" onClick={save} className="w-full">{editing ? "Save Changes" : "Add Transaction"}</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
    </div>
  );
}
